import type { TeactNode } from '../../../lib/teact/teact';
import React from '../../../lib/teact/teact';
import { getActions, withGlobal } from '../../../global';

import type { ApiPeer, ApiUser } from '../../../api/types';
import { ApiMessageEntityTypes } from '../../../api/types';

import { getUserFullName, isUserId } from '../../../global/helpers';
import { selectUser } from '../../../global/selectors';
import { selectIsCurrentUserPrivileged } from '../../../global/selectors/users';

import useAppLayout from '../../../hooks/useAppLayout';

type OwnProps = {
  userId?: string;
  username?: string;
  chatId?: string;
  children: TeactNode;
};

type StateProps = {
  userOrChat?: ApiPeer;
  displayName?: string;
  isCurrentUserPrivileged?: boolean;
  chatId?: string;
};

const MentionLink = ({
  userId,
  username,
  chatId,
  userOrChat,
  displayName,
  isCurrentUserPrivileged,
  children,
}: OwnProps & StateProps) => {
  const {
    openChat,
    openChatByUsername,
    setShouldCloseRightColumn,
    checkCanAddContactInGroupAndOpenProfile,
  } = getActions();

  const { isMobile } = useAppLayout();

  const handleClick = () => {
    if (chatId && isUserId(chatId) && !isCurrentUserPrivileged) {
      return;
    }

    if (isMobile) {
      setShouldCloseRightColumn({
        value: true,
      });
    }

    const targetUserId = userOrChat?.id;
    if (chatId && targetUserId && isUserId(targetUserId) && !isUserId(chatId) && !isCurrentUserPrivileged) {
      checkCanAddContactInGroupAndOpenProfile({ userId: targetUserId, chatId });
      return;
    }

    if (userOrChat) {
      openChat({ id: userOrChat.id });
    } else if (username) {
      openChatByUsername({ username: username.startsWith('@') ? username.substring(1) : username });
    }
  };

  return (
    <a
      onClick={handleClick}
      className="text-entity-link"
      dir="auto"
      data-entity-type={userId ? ApiMessageEntityTypes.MentionName : ApiMessageEntityTypes.Mention}
    >
      {displayName ? `@${displayName}` : children}
    </a>
  );
};

export default withGlobal<OwnProps>(
  (global, { userId, username, chatId }): StateProps => {
    const isCurrentUserPrivileged = selectIsCurrentUserPrivileged(global);
    const base = { chatId, isCurrentUserPrivileged };

    if (userId) {
      return {
        ...base,
        userOrChat: selectUser(global, userId),
      };
    }

    if (username) {
      const cleanUsername = username.startsWith('@') ? username.substring(1).toLowerCase() : username.toLowerCase();
      const matchedUser = Object.values(global.users.byId).find(
        (u: ApiUser) => u.usernames?.some((un) => un.username.toLowerCase() === cleanUsername),
      );
      if (matchedUser) {
        const name = getUserFullName(matchedUser);
        return {
          ...base,
          userOrChat: matchedUser,
          displayName: name || undefined,
        };
      }
    }

    return { ...base };
  },
)(MentionLink);
